import { Currency, MeasurementSystem, RegionalLanguage, TimeFormat } from '@shared/common/types/enums';
import { IFormatSetting } from '@shared/services';

type DistanceUnitLabels = { km: string; miles: string };

const DEFAULT_SETTINGS: IFormatSetting = {
  dateTime: {
    dateFormat: 'MM/DD/YYYY',
    timeFormat: TimeFormat.HOUR_12,
  },
  unitRegional: {
    measurementSystem: MeasurementSystem.IMPERIAL,
    currency: Currency.USD,
    language: RegionalLanguage.EN,
  },
};

const TIME_PATTERNS: Record<TimeFormat, 'HH:mm' | 'h:mm A'> = {
  [TimeFormat.HOUR_24]: 'HH:mm',
  [TimeFormat.HOUR_12]: 'h:mm A',
};

const DISTANCE_LABELS: Partial<Record<RegionalLanguage, DistanceUnitLabels>> = {
  [RegionalLanguage.EN]: { km: 'km', miles: 'miles' },
  [RegionalLanguage.DE]: { km: 'km', miles: 'Meilen' },
  [RegionalLanguage.ES]: { km: 'km', miles: 'millas' },
};

const CURRENCY_SYMBOLS: Record<string, string> = {
  [Currency.USD]: '$',
  [Currency.AUD]: '$',
  [Currency.EUR]: '€',
  [Currency.GBP]: '£',
  [Currency.JPY]: '¥',
  [Currency.CNY]: '¥',
  [Currency.BRL]: 'R$',
};

const RETURN_NULL_ON_UNSUPPORTED_CURRENCY = false;

export class LocationFormattingHelper {
  private readonly CURRENCY_MIN_FRACTION_DIGITS = 2;

  private readonly settings: IFormatSetting;

  private readonly distanceLabels: DistanceUnitLabels;

  constructor(settingsData?: IFormatSetting) {
    this.settings = LocationFormattingHelper.mergeSettings(DEFAULT_SETTINGS, settingsData);
    this.distanceLabels = LocationFormattingHelper.getDistanceLabels(this.settings.unitRegional.language);
  }

  public formatDate(value: Date | string): string | null {
    if (!value) return null;

    const date = LocationFormattingHelper.toValidDate(value);
    if (!date) return null;

    const datePart = LocationFormattingHelper.formatByPattern(date, this.settings.dateTime.dateFormat);
    const timePart = LocationFormattingHelper.formatTime(date, this.timePattern);

    return `${datePart} ${timePart}`;
  }

  public formatMeasurement(value: string | number | null): string | null {
    if (value == null || value === '') return null;

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;

    const isImperial = this.settings.unitRegional.measurementSystem === MeasurementSystem.IMPERIAL;
    const label = isImperial ? this.distanceLabels.miles : this.distanceLabels.km;

    return `${numericValue.toLocaleString('en-US')} ${label}`;
  }

  public formatCurrency(value?: string | number | null): string | null {
    if (value == null || value === '') return null;

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return null;

    const symbol = this.getCurrencySymbol();
    if (!symbol && RETURN_NULL_ON_UNSUPPORTED_CURRENCY) return null;

    const formatted = numericValue.toLocaleString(undefined, {
      minimumFractionDigits: this.CURRENCY_MIN_FRACTION_DIGITS,
      maximumFractionDigits: 2,
    });

    return `${symbol}${formatted}`;
  }

  private getCurrencySymbol(): string {
    const symbol = CURRENCY_SYMBOLS[this.settings.unitRegional.currency];
    if (symbol) return symbol;

    return RETURN_NULL_ON_UNSUPPORTED_CURRENCY ? '' : '$';
  }

  private get timePattern(): 'HH:mm' | 'h:mm A' {
    return TIME_PATTERNS[this.settings.dateTime.timeFormat] ?? TIME_PATTERNS[TimeFormat.HOUR_12];
  }

  private static getDistanceLabels(language: RegionalLanguage): DistanceUnitLabels {
    return DISTANCE_LABELS[language] ?? DISTANCE_LABELS[RegionalLanguage.EN] ?? { km: 'km', miles: 'miles' };
  }

  private static mergeSettings(base: IFormatSetting, patch?: IFormatSetting): IFormatSetting {
    if (!patch) return base;

    return {
      dateTime: {
        ...base.dateTime,
        ...patch.dateTime,
      },
      unitRegional: {
        ...base.unitRegional,
        ...patch.unitRegional,
      },
    };
  }

  private static toValidDate(value: Date | string): Date | null {
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  private static formatByPattern(date: Date, pattern: string): string {
    const yyyy = String(date.getFullYear());
    const mm = LocationFormattingHelper.pad2(date.getMonth() + 1);
    const dd = LocationFormattingHelper.pad2(date.getDate());

    const replacements: Array<[string, string]> = [
      ['YYYY', yyyy],
      ['MM', mm],
      ['DD', dd],
    ];

    return replacements.reduce((acc, [token, value]) => acc.split(token).join(value), pattern);
  }

  private static formatTime(date: Date, pattern: 'HH:mm' | 'h:mm A'): string {
    const minutes = LocationFormattingHelper.pad2(date.getMinutes());

    if (pattern === 'HH:mm') {
      const hours24 = LocationFormattingHelper.pad2(date.getHours());
      return `${hours24}:${minutes}`;
    }

    const rawHours = date.getHours();
    const isPm = rawHours >= 12;
    const hours12 = rawHours % 12 === 0 ? 12 : rawHours % 12;
    const suffix = isPm ? 'PM' : 'AM';

    return `${hours12}:${minutes} ${suffix}`;
  }

  private static pad2(n: number): string {
    return n < 10 ? `0${n}` : String(n);
  }
}
